import { useEffect, useState } from "react";
import { CancelButton } from "../assets/Buttons";
import {
  dmsOptions,
  ledUsageOptions,
  reminderValues,
  STATUS_OPTIONS,
} from "../contexts/Constants";
import { useGlobalContext } from "../contexts/Constants";
import {
  Container,
  SixthBox,
  Form,
  FullBox,
  Label,
  QuarterBox,
  SelectInput,
  SubmitButton,
  TextArea,
  TextInput,
  Loading,
  SuggestionsList,
  SuggestionItem,
  AutocompleteWrapper,
  Suggestion,
  PlaceDetails,
  Buttons,
} from "./FormElements";
import axios from "axios";

const FormPage = ({
  handleSubmit,
  edit,
}: {
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  edit: boolean;
}) => {
  const { setFormData, formData, setSelectedPageState, isMobile, loggedIn } =
    useGlobalContext();
  const [address, setAddress] = useState<string>("");
  const [addressSuggestions, setAddressSuggestions] = useState<Suggestion[]>(
    []
  );
  const [addressIsLoading, setAddressIsLoading] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [query, setQuery] = useState<string>("");

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.length < 3) {
        setAddressSuggestions([]);
        return;
      }

      setAddressIsLoading(true);
      try {
        const response = await axios.get("/api/autocomplete", {
          headers: {
            Authorization: `Bearer ${loggedIn.token}`,
          },
          params: { input: query },
        });
        setAddressSuggestions(response.data.suggestions);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setAddressSuggestions([]);
      } finally {
        setAddressIsLoading(false);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [query, loggedIn]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!addressSuggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev < addressSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter" && focusedIndex >= 0) {
      e.preventDefault();
      handleSelect(addressSuggestions[focusedIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setAddressSuggestions([]);
      setFocusedIndex(-1);
    }
  };

  const handleSelect = async (suggestion: Suggestion) => {
    setAddress(suggestion.description);
    setQuery("");
    setAddressSuggestions([]);
    setFocusedIndex(-1);

    try {
      const response = await axios.get("/api/place-details", {
        params: { place_id: suggestion.place_id },
      });
      const details: PlaceDetails = response.data;
      setFormData({
        ...formData,
        street: details.address || suggestion.description,
        city: details.city || formData.city || "",
        state: details.state || formData.state || "",
        postCode: details.postcode || formData.postCode || "",
        country: details.country || formData.country || "",
      });
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    let value = e.target.value;
    if (e.target.name === "phone" && value.length) {
      let startsPlus = false;
      let filteredValue = value;
      if (value[0] === "+" || value.startsWith("(+")) {
        filteredValue = value.replace(/^\(+/, "");
        startsPlus = true;
      }
      filteredValue = filteredValue.replace(/\D/g, "");
      if (startsPlus ? filteredValue.length > 11 : filteredValue.length > 10) {
        return;
      }
      const finalValue = startsPlus ? `+${filteredValue}` : filteredValue;
      setFormData({
        ...formData,
        phone: finalValue,
      });
      return;
    }
    setFormData({
      ...formData,
      [e.target.name]: value.trim(), // Trim for other fields
    });
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit} autoComplete="off">
        <QuarterBox>
          <Label htmlFor="PitchAddBusinessName">Business Name*</Label>
          <TextInput
            name="businessName"
            defaultValue={formData.businessName}
            onChange={handleChange}
            placeholder="Add Business Name"
            type="text"
            disabled={edit}
            required
          ></TextInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddContactName">Contact Name*</Label>
          <TextInput
            name="contactName"
            defaultValue={formData.contactName}
            onChange={handleChange}
            placeholder="Add Contact Name"
            type="text"
            required
          ></TextInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddPosition">Position*</Label>
          <SelectInput
            name="position"
            defaultValue={formData.position}
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Add Positon
            </option>
            <option value="other">Other</option>
            <option value="dealerPrincipal">Dealer Principal</option>
            <option value="generalManager">General Manager</option>
            <option value="marketingManager">Marketing Manager</option>
            <option value="reception">Reception</option>
            <option value="salesManager">Sales Manager</option>
          </SelectInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddReminderDuration">Follow Up Reminder*</Label>
          <SelectInput
            name="reminderDuration"
            defaultValue={formData.reminderDuration}
            id="PitchAddReminderDuration"
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Select Reminder Duration
            </option>
            {reminderValues.map((value) => (
              <option value={value} key={value}>
                {value}
              </option>
            ))}
          </SelectInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddContactType">Contact Type*</Label>
          <SelectInput
            name="contactType"
            defaultValue={formData.contactType}
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Add Contact Type
            </option>
            <option value="review">Review</option>
            <option value="phone">Phone</option>
            <option value="email">Email</option>
            <option value="intro">Intro</option>
            <option value="follow">Follow</option>
            <option value="inPerson">In Person</option>
          </SelectInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddPhone">Phone*</Label>
          <TextInput
            name="phone"
            onChange={handleChange}
            placeholder="Add Phone Number"
            type="tel"
            value={formData.phone}
            pattern="[0-9]*"
            required
          ></TextInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddEmail">Email</Label>
          <TextInput
            name="email"
            defaultValue={formData.email}
            onChange={handleChange}
            placeholder="Add Email"
            type="text"
          ></TextInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddWebsite">Website</Label>
          <TextInput
            name="website"
            defaultValue={formData.website}
            onChange={handleChange}
            placeholder="Add Website"
            type="text"
          ></TextInput>
        </QuarterBox>
        <FullBox>
          <Label htmlFor="PitchAddAddress">Address</Label>
          <AutocompleteWrapper>
            <TextInput
              onChange={(e) => {
                const value = e.target.value;
                setAddress(value);
                setQuery(value);
                setFocusedIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Add Address"
              type="text"
              value={address}
              id="PitchAddAddress"
            />
            {addressIsLoading && <Loading>Loading...</Loading>}
            {addressSuggestions.length > 0 && (
              <SuggestionsList>
                {addressSuggestions.map((suggestion, index) => (
                  <SuggestionItem
                    key={suggestion.place_id}
                    onClick={() => handleSelect(suggestion)}
                    style={{
                      backgroundColor:
                        index === focusedIndex ? "#f0f0f0" : "transparent",
                    }}
                  >
                    {suggestion.description}
                  </SuggestionItem>
                ))}
              </SuggestionsList>
            )}
          </AutocompleteWrapper>
        </FullBox>
        {isMobile ? (
          <FullBox>
            <Label htmlFor="PitchAddStreet">Street</Label>
            <TextInput
              name="street"
              defaultValue={formData.street}
              onChange={handleChange}
              placeholder="Add Street"
              type="text"
            ></TextInput>
          </FullBox>
        ) : (
          <QuarterBox>
            <Label htmlFor="PitchAddStreet">Street</Label>
            <TextInput
              name="street"
              defaultValue={formData.street}
              onChange={handleChange}
              placeholder="Add Street"
              type="text"
            ></TextInput>
          </QuarterBox>
        )}
        <QuarterBox>
          <Label htmlFor="PitchAddCity">City</Label>
          <TextInput
            name="city"
            defaultValue={formData.city}
            onChange={handleChange}
            placeholder="Add City"
            type="text"
          ></TextInput>
        </QuarterBox>
        <SixthBox>
          <Label htmlFor="PitchAddState">State</Label>
          <TextInput
            name="state"
            defaultValue={formData.state}
            onChange={handleChange}
            placeholder="Add State"
            type="text"
          ></TextInput>
        </SixthBox>
        <SixthBox>
          <Label htmlFor="PitchAddPostCode">Post Code</Label>
          <TextInput
            name="postCode"
            defaultValue={formData.postCode}
            onChange={handleChange}
            placeholder="Add Post Code"
            type="text"
          ></TextInput>
        </SixthBox>
        <SixthBox>
          <Label htmlFor="PitchAddCountry">Country</Label>
          <TextInput
            name="country"
            defaultValue={formData.country}
            onChange={handleChange}
            placeholder="Add Country"
            type="country"
          ></TextInput>
        </SixthBox>
        <QuarterBox>
          <Label htmlFor="PitchAddDms">DMS</Label>
          <SelectInput
            name="dms"
            defaultValue={formData.dms}
            onChange={handleChange}
          >
            <option value="">Select DMS</option>
            {dmsOptions.map((option) => (
              <option value={option} key={option}>
                {option}
              </option>
            ))}
          </SelectInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddLedUsage">LED Usage</Label>
          <SelectInput
            name="ledUsage"
            defaultValue={formData.ledUsage}
            onChange={handleChange}
          >
            <option value="" disabled>
              Add LED Usage
            </option>
            {ledUsageOptions.map((option) => (
              <option value={option} key={option}>
                {option}
              </option>
            ))}
          </SelectInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddStatus">Status*</Label>
          <SelectInput
            name="status"
            defaultValue={formData.status}
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Add Status
            </option>
            {STATUS_OPTIONS.map((status) => (
              <option value={status.status} key={status.status}>
                {status.status}
              </option>
            ))}
          </SelectInput>
        </QuarterBox>
        <QuarterBox>
          <Label htmlFor="PitchAddContactSentiment">Contact Sentiment*</Label>
          <SelectInput
            name="contactSentiment"
            defaultValue={formData.contactSentiment}
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Choose Sentiment
            </option>
            <option value="positive">Positive</option>
            <option value="neutral">Neutral</option>
            <option value="negative">Negative</option>
          </SelectInput>
        </QuarterBox>
        <FullBox>
          <Label htmlFor="PitchAddNote">Note</Label>
          <TextArea
            name="note"
            defaultValue={formData.note}
            onChange={handleChange}
            placeholder="Add Notes on Rates Discuessed, ETC.."
          ></TextArea>
        </FullBox>
        <Buttons>
          <SubmitButton type="submit">Save</SubmitButton>
          <CancelButton
            type="button"
            onClick={() => setSelectedPageState("viewAll")}
          >
            Cancel
          </CancelButton>
        </Buttons>
      </Form>
    </Container>
  );
};

export default FormPage;
